import { AppRouteNames } from "@/shared/@types/AppRouteNames";
import { useNotifications } from "@/shared/composables/useNotifications.composable";
import {
  clearImpersonationCookie,
  getImpersonatedUserIdFromCookie,
} from "@/shared/helper/impersonationCookie.helper";
import { DEV_JWT_KEY } from "@/shared/helper/constants.helper";
import { isDevelopment } from "@/shared/helper/environment.helper";
import { translateError } from "@/shared/helper/translateError.helper";
import { i18n } from "@/shared/i18n/i18n";
import { logoutCurrentUser } from "@/shared/services/logoutCurrentUser.service";
import { useAccountStore } from "@/shared/stores/AccountStore";
import { useSelectedCustomerStore } from "@/shared/stores/SelectedCustomerStore";
import { useAppStateStore } from "@/shared/stores/AppStateStore";
import { useFeatureFlagsStore } from "@/shared/stores/FeatureFlagsStore";
import { logout as salesforceLogout } from "@/shared/auth/services/logout.service";
import { hasAuthenticatedUser } from "./hasAuthenticatedUser.helper";

// We cannot use useRouter or useI18n here as it is called in the beforeEach hook of the router, which is outside of the app context.
export const useLogout = () => {
  const accountStore = useAccountStore();
  const selectedCustomerStore = useSelectedCustomerStore();
  const featureFlagsStore = useFeatureFlagsStore();

  const logout = async () => {
    const { addErrorSnackbar } = useNotifications();
    const { showLoadingOverlay, hideLoadingOverlay } = useAppStateStore();
    showLoadingOverlay(i18n.t("shared.component.loadingOverlay.logout"));

    const isAuthenticatedWithJwt = await hasAuthenticatedUser();

    // Notify the phoenix backend about the logout
    try {
      if (isAuthenticatedWithJwt || isDevelopment) await logoutCurrentUser();
      accountStore.resetData();
      selectedCustomerStore.resetData();
      featureFlagsStore.resetData();
    } catch (error) {
      hideLoadingOverlay();
      addErrorSnackbar(error as string);
      return;
    }

    // Remove the dev token from local storage
    localStorage.removeItem(DEV_JWT_KEY);

    // Remove impersonation cookie
    const isImpersonatingUser = !!getImpersonatedUserIdFromCookie();
    if (isImpersonatingUser) {
      clearImpersonationCookie();
    }

    // Logout from Salesforce
    if (isAuthenticatedWithJwt) {
      try {
        await salesforceLogout();
      } catch {
        hideLoadingOverlay();
        addErrorSnackbar(translateError("INTERNAL_ERROR"));
        return;
      }
    }

    // Redirect to applicable page
    if (isDevelopment) {
      window.location.assign(`/${AppRouteNames.DevLogin}`);
    } else {
      window.location.assign(`/${AppRouteNames.Login}`);
    }
  };

  return {
    logout,
  };
};
